// ─────────────────────────────────────────────
import { useTranslation } from "@/hooks/useTranslation";
import { pickLocalized } from "@/lib/localized";
// MultiSubFieldQuestion
//
// Grid of select dropdowns per sub-field.
// Design: §6.5 Question Type → MULTI_SUB_FIELD
// PRD §6.3: CIA Triad multi-sub-field risk matrix
// ─────────────────────────────────────────────

export interface SubFieldOption {
  value: string;
  label: string;
  labelSr?: string;
  score: number;
}

export interface SubField {
  name: string;
  nameSr?: string;
  options: SubFieldOption[];
}

interface MultiSubFieldQuestionProps {
  questionId: string;
  subFields: SubField[];
  value: Record<string, string> | null;
  onChange: (value: Record<string, string>) => void;
  readOnly?: boolean;
}

export function MultiSubFieldQuestion({
  questionId,
  subFields,
  value,
  onChange,
  readOnly,
}: MultiSubFieldQuestionProps) {
  const { language } = useTranslation();
  if (!subFields || subFields.length === 0) {
    return (
      <p className="text-[13px] text-text-muted italic">No sub-fields defined.</p>
    );
  }

  function handleChange(fieldName: string, fieldValue: string) {
    const updated = { ...(value ?? {}), [fieldName]: fieldValue };
    onChange(updated);
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
      {subFields.map((sf) => (
        <div key={sf.name} className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-text-secondary">{pickLocalized(sf.name, sf.nameSr, language)}</label>
          <select
            value={value?.[sf.name] ?? ""}
            onChange={(e) => handleChange(sf.name, e.target.value)}
            disabled={readOnly}
            className="h-9 w-full rounded-control border border-border-default bg-bg-input px-3 text-[13px] text-text-primary focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent disabled:opacity-50"
            id={`${questionId}-${sf.name}`}
          >
            <option value="">Select...</option>
            {sf.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {pickLocalized(opt.label, opt.labelSr, language)}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}
